import React from "react";
import ReactDOM from "react-dom/client";
import App from "src/App";
import { Provider } from "react-redux";
import { initReduxStore } from "@redux/store";
import { InMemorySharedRepository } from "@shared-kernel/secondary/shared/shared-repository/in-memory-shared-repository";
import { InMemoryTeacherRepository } from "@shared-kernel/secondary/teacher/in-memory-teacher-repository";
import { CookieTokenStorageGateway } from "@shared-kernel/secondary/cookies-token-storage-gateway";
import { BackendTeacherRepository } from "@shared-kernel/secondary/teacher/backend-teacher-repository";
import { BackendStudentRepository } from "@shared-kernel/secondary/student/student-repository/backend-student-repository";
import { InMemoryStudentRepository } from "@shared-kernel/secondary/student/student-repository/in-memory-student-repository";
import { BackendSharedRepository } from "@shared-kernel/secondary/shared/shared-repository/backend-shared-repository";
import { initCommonAdapters } from "./common-adapters";
import { RealNumberGenerator } from "@shared-kernel/secondary/number-generator/real-number-generator";
import { InMemoryProviderRepository } from "@shared-kernel/secondary/provider/provider-repository/in-memory-provider-repository";
import { BackendProviderRepository } from "@shared-kernel/secondary/provider/provider-repository/backend-provider-repository";
import { RealDateProvider } from "@shared-kernel/secondary/date-provider/real-date-provider";
import { InMemoryDocumentUploadProvider } from "@shared-kernel/secondary/shared/document-upload-provider/in-memory-document-upload-provider";
import { BackendDocumentUploadProvider } from "@shared-kernel/secondary/shared/document-upload-provider/backend-document-upload-provider";
import { BackendEducationalAdvisorRepository } from "@shared-kernel/secondary/educational-advisor/backend-educational-advisor-repository";
import { InMemoryEducationalAdvisorRepository } from "@shared-kernel/secondary/educational-advisor/in-memory-educational-advisor-repository";
import { BackendAdminRepository } from "@shared-kernel/secondary/admin/admin-repository/backend-admin-repository";
import { InMemoryAdminRepository } from "@shared-kernel/secondary/admin/admin-repository/in-memory-admin-repository";
import { BackendPublicRepository } from "@shared-kernel/secondary/public/backend-public-repository";
import { InMemoryPublicRepository } from "@shared-kernel/secondary/public/in-memory-public-repository";
import { GouvSiretSearchRepository } from "@shared-kernel/secondary/siret-search/gouv-siret-search-repository";
import { InMemorySiretSearchRepository } from "@shared-kernel/secondary/siret-search/in-memory-siret-search-repository";

let providerRepository;
let sharedRepository;
let documentUploadProvider;
let studentRepository;
let educationalAdvisorRepository;
let adminRepository;
let publicRepository;
let siretSearchRepository;

const tokenStorageGateway = new CookieTokenStorageGateway();

let teacherRepository;

let store: ReturnType<typeof initReduxStore>;

async function initializeApp() {
  if ("demo" === import.meta.env.MODE) {
    // Dynamically import mock data only when needed
    const [
      { students, studentsWithBalance },
      { users },
      { teacherListElement, teachers },
      { transfers },
      { balanceAdjustments },
      { providers },
    ] = await Promise.all([
      import("./mocks/students"),
      import("./mocks/users"),
      import("./mocks/teachers"),
      import("./mocks/transfers"),
      import("./mocks/balance-adjustments"),
      import("./mocks/providers"),
    ]);

    sharedRepository = new InMemorySharedRepository();
    teacherRepository = new InMemoryTeacherRepository();
    providerRepository = new InMemoryProviderRepository();
    studentRepository = new InMemoryStudentRepository();
    documentUploadProvider = new InMemoryDocumentUploadProvider();
    educationalAdvisorRepository = new InMemoryEducationalAdvisorRepository();
    publicRepository = new InMemoryPublicRepository();
    publicRepository.feedWithUsers(...users);
    siretSearchRepository = new InMemorySiretSearchRepository();

    adminRepository = new InMemoryAdminRepository();
    adminRepository.feedWithStudents(...students);
    adminRepository.feedWithStudentListElement(...studentsWithBalance);
    adminRepository.feedWithTeachers(...teachers);
    adminRepository.feedWithTeacherListElement(...teacherListElement);
    adminRepository.feedWithTransfers(...transfers);
    adminRepository.feedWithBalanceAdjustments(...balanceAdjustments);
    adminRepository.feedWithProviders(...providers);
  } else {
    sharedRepository = new BackendSharedRepository(tokenStorageGateway);
    teacherRepository = new BackendTeacherRepository(tokenStorageGateway);
    providerRepository = new BackendProviderRepository(tokenStorageGateway);
    studentRepository = new BackendStudentRepository(tokenStorageGateway);
    documentUploadProvider = new BackendDocumentUploadProvider(tokenStorageGateway);
    educationalAdvisorRepository = new BackendEducationalAdvisorRepository(tokenStorageGateway);
    adminRepository = new BackendAdminRepository(tokenStorageGateway);
    publicRepository = new BackendPublicRepository(tokenStorageGateway);
    siretSearchRepository = new GouvSiretSearchRepository();
  }

  store = initReduxStore({
    sharedRepository,
    teacherRepository,
    studentRepository,
    providerRepository,
    documentUploadProvider,
    educationalAdvisorRepository,
    adminRepository,
    publicRepository,
    siretSearchRepository,
  });

  initCommonAdapters({
    numberGenerator: new RealNumberGenerator(),
    dateProvider: new RealDateProvider(),
  });

  const root = ReactDOM.createRoot(document.getElementById("main") as HTMLElement);

  root.render(
    // Be careful, this mode renders elements twice in local dev
    <React.StrictMode>
      <Provider store={store}>
        <App />
      </Provider>
    </React.StrictMode>,
  );
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Initialize the app
initializeApp();
